import type { Vec3 } from './vec3'
import type { Quat } from './quat'

/**
 * 4x4 matrix, column-major (OpenGL / three.js / MediaPipe face-geometry convention).
 * m[0..3] is the first column; translation lives in m[12], m[13], m[14].
 */
export type Mat4 = Float32Array

export const mat4 = {
  create(): Mat4 {
    const m = new Float32Array(16)
    m[0] = m[5] = m[10] = m[15] = 1
    return m
  },
  identity(out: Mat4): Mat4 {
    out.fill(0)
    out[0] = out[5] = out[10] = out[15] = 1
    return out
  },
  clone(m: Mat4): Mat4 {
    return new Float32Array(m)
  },
  copy(out: Mat4, m: ArrayLike<number>): Mat4 {
    for (let i = 0; i < 16; i++) out[i] = m[i]!
    return out
  },
  fromArray(a: ArrayLike<number>, offset = 0): Mat4 {
    const m = new Float32Array(16)
    for (let i = 0; i < 16; i++) m[i] = a[offset + i]!
    return m
  },
  transpose(out: Mat4, m: Mat4): Mat4 {
    if (out === m) {
      const t = new Float32Array(m)
      m = t
    }
    for (let r = 0; r < 4; r++) for (let c = 0; c < 4; c++) out[c * 4 + r] = m[r * 4 + c]!
    return out
  },
  /** out = a * b */
  multiply(out: Mat4, a: Mat4, b: Mat4): Mat4 {
    const a00 = a[0]!, a01 = a[1]!, a02 = a[2]!, a03 = a[3]!
    const a10 = a[4]!, a11 = a[5]!, a12 = a[6]!, a13 = a[7]!
    const a20 = a[8]!, a21 = a[9]!, a22 = a[10]!, a23 = a[11]!
    const a30 = a[12]!, a31 = a[13]!, a32 = a[14]!, a33 = a[15]!
    let b0 = b[0]!, b1 = b[1]!, b2 = b[2]!, b3 = b[3]!
    out[0] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30
    out[1] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31
    out[2] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32
    out[3] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33
    b0 = b[4]!; b1 = b[5]!; b2 = b[6]!; b3 = b[7]!
    out[4] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30
    out[5] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31
    out[6] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32
    out[7] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33
    b0 = b[8]!; b1 = b[9]!; b2 = b[10]!; b3 = b[11]!
    out[8] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30
    out[9] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31
    out[10] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32
    out[11] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33
    b0 = b[12]!; b1 = b[13]!; b2 = b[14]!; b3 = b[15]!
    out[12] = b0 * a00 + b1 * a10 + b2 * a20 + b3 * a30
    out[13] = b0 * a01 + b1 * a11 + b2 * a21 + b3 * a31
    out[14] = b0 * a02 + b1 * a12 + b2 * a22 + b3 * a32
    out[15] = b0 * a03 + b1 * a13 + b2 * a23 + b3 * a33
    return out
  },
  /** General 4x4 inverse. Returns null if singular. */
  invert(out: Mat4, m: Mat4): Mat4 | null {
    const a00 = m[0]!, a01 = m[1]!, a02 = m[2]!, a03 = m[3]!
    const a10 = m[4]!, a11 = m[5]!, a12 = m[6]!, a13 = m[7]!
    const a20 = m[8]!, a21 = m[9]!, a22 = m[10]!, a23 = m[11]!
    const a30 = m[12]!, a31 = m[13]!, a32 = m[14]!, a33 = m[15]!
    const b00 = a00 * a11 - a01 * a10
    const b01 = a00 * a12 - a02 * a10
    const b02 = a00 * a13 - a03 * a10
    const b03 = a01 * a12 - a02 * a11
    const b04 = a01 * a13 - a03 * a11
    const b05 = a02 * a13 - a03 * a12
    const b06 = a20 * a31 - a21 * a30
    const b07 = a20 * a32 - a22 * a30
    const b08 = a20 * a33 - a23 * a30
    const b09 = a21 * a32 - a22 * a31
    const b10 = a21 * a33 - a23 * a31
    const b11 = a22 * a33 - a23 * a32
    let det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06
    if (!det) return null
    det = 1 / det
    out[0] = (a11 * b11 - a12 * b10 + a13 * b09) * det
    out[1] = (a02 * b10 - a01 * b11 - a03 * b09) * det
    out[2] = (a31 * b05 - a32 * b04 + a33 * b03) * det
    out[3] = (a22 * b04 - a21 * b05 - a23 * b03) * det
    out[4] = (a12 * b08 - a10 * b11 - a13 * b07) * det
    out[5] = (a00 * b11 - a02 * b08 + a03 * b07) * det
    out[6] = (a32 * b02 - a30 * b05 - a33 * b01) * det
    out[7] = (a20 * b05 - a22 * b02 + a23 * b01) * det
    out[8] = (a10 * b10 - a11 * b08 + a13 * b06) * det
    out[9] = (a01 * b08 - a00 * b10 - a03 * b06) * det
    out[10] = (a30 * b04 - a31 * b02 + a33 * b00) * det
    out[11] = (a21 * b02 - a20 * b04 - a23 * b00) * det
    out[12] = (a11 * b07 - a10 * b09 - a12 * b06) * det
    out[13] = (a00 * b09 - a01 * b07 + a02 * b06) * det
    out[14] = (a31 * b01 - a30 * b03 - a32 * b00) * det
    out[15] = (a20 * b03 - a21 * b01 + a22 * b00) * det
    return out
  },
  /** Compose translation * rotation * scale. */
  compose(out: Mat4, t: Vec3, q: Quat, s: Vec3 | number): Mat4 {
    const x = q[0], y = q[1], z = q[2], w = q[3]
    const x2 = x + x, y2 = y + y, z2 = z + z
    const xx = x * x2, xy = x * y2, xz = x * z2
    const yy = y * y2, yz = y * z2, zz = z * z2
    const wx = w * x2, wy = w * y2, wz = w * z2
    const sx = typeof s === 'number' ? s : s[0]
    const sy = typeof s === 'number' ? s : s[1]
    const sz = typeof s === 'number' ? s : s[2]
    out[0] = (1 - (yy + zz)) * sx
    out[1] = (xy + wz) * sx
    out[2] = (xz - wy) * sx
    out[3] = 0
    out[4] = (xy - wz) * sy
    out[5] = (1 - (xx + zz)) * sy
    out[6] = (yz + wx) * sy
    out[7] = 0
    out[8] = (xz + wy) * sz
    out[9] = (yz - wx) * sz
    out[10] = (1 - (xx + yy)) * sz
    out[11] = 0
    out[12] = t[0]
    out[13] = t[1]
    out[14] = t[2]
    out[15] = 1
    return out
  },
  fromTranslation(out: Mat4, t: Vec3): Mat4 {
    mat4.identity(out)
    out[12] = t[0]
    out[13] = t[1]
    out[14] = t[2]
    return out
  },
  getTranslation(out: Vec3, m: Mat4): Vec3 {
    out[0] = m[12]!
    out[1] = m[13]!
    out[2] = m[14]!
    return out
  },
  getScale(out: Vec3, m: Mat4): Vec3 {
    out[0] = Math.hypot(m[0]!, m[1]!, m[2]!)
    out[1] = Math.hypot(m[4]!, m[5]!, m[6]!)
    out[2] = Math.hypot(m[8]!, m[9]!, m[10]!)
    return out
  },
  /** Transform a point (w = 1), with perspective divide if needed. */
  transformPoint(out: Vec3, m: Mat4, p: Vec3): Vec3 {
    const x = p[0], y = p[1], z = p[2]
    let w = m[3]! * x + m[7]! * y + m[11]! * z + m[15]!
    w = w || 1
    out[0] = (m[0]! * x + m[4]! * y + m[8]! * z + m[12]!) / w
    out[1] = (m[1]! * x + m[5]! * y + m[9]! * z + m[13]!) / w
    out[2] = (m[2]! * x + m[6]! * y + m[10]! * z + m[14]!) / w
    return out
  },
  /** Transform a direction (w = 0), ignoring translation. */
  transformDirection(out: Vec3, m: Mat4, d: Vec3): Vec3 {
    const x = d[0], y = d[1], z = d[2]
    out[0] = m[0]! * x + m[4]! * y + m[8]! * z
    out[1] = m[1]! * x + m[5]! * y + m[9]! * z
    out[2] = m[2]! * x + m[6]! * y + m[10]! * z
    return out
  },
  /** OpenGL perspective projection. fovY in radians. */
  perspective(out: Mat4, fovY: number, aspect: number, near: number, far: number): Mat4 {
    const f = 1 / Math.tan(fovY / 2)
    out.fill(0)
    out[0] = f / aspect
    out[5] = f
    out[10] = (far + near) / (near - far)
    out[11] = -1
    out[14] = (2 * far * near) / (near - far)
    return out
  },
  equals(a: Mat4, b: Mat4, eps = 1e-5): boolean {
    for (let i = 0; i < 16; i++) if (Math.abs(a[i]! - b[i]!) > eps) return false
    return true
  },
}
